import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VirtualizedAutocomplete from '../../../../../../components/Dropdown/VirtualizedAutocomplete';
import { DeviceTypes, DeviceStatusOptions } from '../../../Device List/config/deviceConfig';

const BuildingDeviceTable = ({
  assignedDevices,
  setAssignedDevices,
  deviceOptions,
  newDeviceId,
  setNewDeviceId,
  addDevice,
  onUpdateDevice,
  onRemoveDevice,
  isEditMode,
  toggleNewDeviceForm,
}) => {
  // Toggle the isEditing state for a specific device
  const handleEditClick = (index) => {
    setAssignedDevices((prevDevices) =>
      prevDevices.map((device, i) =>
        i === index ? { ...device, isEditing: true } : device
      )
    );
  };

  // Cancel edit mode and reset changes for a specific device
  const handleCancelClick = (index) => {
    setAssignedDevices((prevDevices) =>
      prevDevices.map((device, i) =>
        i === index ? { ...device, isEditing: false } : device
      )
    );
  };

  // Handle input changes for a specific device
  const handleChange = (e, index) => {
    const { name, value } = e.target;
    setAssignedDevices((prevDevices) =>
      prevDevices.map((device, i) =>
        i === index ? { ...device, [name]: value } : device
      )
    );
  };

  const handleDeviceSelectionChange = (event, newValue) => {
    setNewDeviceId(newValue ? newValue.key : '');
  };

  return (
    <div>
      {isEditMode && (
        <div className="flex mb-4 items-center">
          <div className="w-1/3 mr-8 ">
            <VirtualizedAutocomplete
              options={deviceOptions
                .filter(
                  (device) =>
                    !assignedDevices.some((d) => d.deviceId === device.deviceId)
                )
                .map((device) => ({
                  key: device.deviceId.toString(),
                  value: `ID: ${device.deviceId} - Type: ${
                    device.deviceType || 'N/A'
                  }`,
                }))}
              value={
                deviceOptions.find((device) => device.deviceId === newDeviceId) ||
                null
              }
              onChange={handleDeviceSelectionChange}
              label="Select Device"
              optionKey="key"
              optionLabel="value"
              fullWidth
            />
          </div>
          <button
            className="px-4 py-2 bg-sss-blue text-white rounded-md mr-4 font-semibold"
            onClick={addDevice}
          >
            Add Device
          </button>
          <button
            className="px-4 py-2 bg-sss-accent-green text-white rounded-md font-semibold"
            onClick={toggleNewDeviceForm}
          >
            New Device
          </button>
        </div>
      )}
      <table className="w-full bg-white rounded-lg overflow-hidden shadow-lg border-2 border-gray-300">
        <thead className="bg-gray-200">
          <tr>
            {isEditMode && <th className="px-4 py-2 border"></th>}
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              Device ID
            </th>
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              Device Type
            </th>
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              Device Status
            </th>
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              Device Domain
            </th>
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              Block
            </th>
            <th className="px-4 py-2 border text-left font-bold text-sss-blue-sel">
              BIN
            </th>
            {isEditMode && <th className="px-4 py-2 border"></th>}
          </tr>
        </thead>
        <tbody className="text-gray-800">
          {assignedDevices.map((device, index) => (
            <tr
              key={device.deviceId || index}
              className="border-t hover:bg-gray-100 transition duration-200"
            >
              {isEditMode && (
                <td className="p-1 border bg-white text-center">
                  {device.isEditing ? (
                    <>
                      <button
                        onClick={() => {
                          onUpdateDevice(device.deviceId, device);
                          handleCancelClick(index);
                        }}
                        className="text-green-500 hover:bg-gray-300 rounded-full"
                      >
                        <CheckIcon />
                      </button>
                      <button
                        onClick={() => handleCancelClick(index)}
                        className="text-red-500 hover:bg-gray-300 rounded-full"
                      >
                        <CloseIcon />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleEditClick(index)}
                      className="border-gray-200 hover:bg-gray-100 rounded-xl transition ease-in-out border-2 p-2"
                    >
                      <EditIcon />
                    </button>
                  )}
                </td>
              )}
              {device.isEditing ? (
                <>
                  <td className="px-4 py-2 border">{device.deviceId || 'N/A'}</td>
                  <td className="px-4 py-2 border">
                    <select
                      name="deviceType"
                      value={device.deviceType || ''}
                      onChange={(e) => handleChange(e, index)}
                      className="border border-gray-300 rounded-md p-1 w-full"
                    >
                      <option value="">Select Device Type</option>
                      {DeviceTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 border">
                    <select
                      name="deviceStatus"
                      value={device.deviceStatus || ''}
                      onChange={(e) => handleChange(e, index)}
                      className="border border-gray-300 rounded-md p-1 w-full"
                    >
                      <option value="">Select Status</option>
                      {DeviceStatusOptions.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2 border">
                    <input
                      name="deviceDomain"
                      value={device.deviceDomain || ''}
                      onChange={(e) => handleChange(e, index)}
                      className="w-full border rounded-md p-1"
                    />
                  </td>
                  <td className="px-4 py-2 border">
                    <input
                      name="block"
                      value={device.block || ''}
                      onChange={(e) => handleChange(e, index)}
                      className="w-full border rounded-md p-1"
                    />
                  </td>
                  <td className="px-4 py-2 border">
                    <input
                      name="bin"
                      value={device.bin || ''}
                      onChange={(e) => handleChange(e, index)}
                      className="w-full border rounded-md p-1"
                    />
                  </td>
                </>
              ) : (
                <>
                  <td className="px-4 py-2 border bg-white">
                    {device.deviceId || 'N/A'}
                  </td>
                  <td className="px-4 py-2 border bg-white">
                    {device.deviceType || 'N/A'}
                  </td>
                  <td className="px-4 py-2 border bg-white">
                    {device.deviceStatus || 'N/A'}
                  </td>
                  <td className="px-4 py-2 border bg-white">
                    {device.deviceDomain || 'N/A'}
                  </td>
                  <td className="px-4 py-2 border bg-white">
                    {device.block || 'N/A'}
                  </td>
                  <td className="px-4 py-2 border bg-white">
                    {device.bin || 'N/A'}
                  </td>
                </>
              )}
              {isEditMode && (
                <td className="p-1 border bg-white text-center">
                  <button
                    onClick={() => onRemoveDevice(index)}
                    className="bg-red-400 hover:bg-red-500 text-white rounded-xl transition ease-in-out border-2 p-2"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BuildingDeviceTable;
